import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  LinearProgress,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import {
  Close as CloseIcon,
  GridView as GridViewIcon,
  ViewModule as ViewModuleIcon,
  ViewList as ViewListIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Visibility as PreviewIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  BrokenImage as BrokenImageIcon,
} from "@mui/icons-material";
import { v4 as uuid_v4 } from "../../../libs/uuid";
import { myToast } from "../../../libs";
import config from "../../../config";
import PreviewFile from "../../../components/files/PreviewFile";
import {
  addressBookPanelHeaderSx,
} from "../../addressbook/panels/panelChrome";
import {
  apiCreateFile,
  apiGetFiles,
  apiUpdateFile,
  apiDeleteFile,
} from "../../../graphQL/file";
import { apiGenerateSatelliteImage } from "../../../graphQL/properties/collections/propertyAssets";

const IMAGE_CATEGORY = "Photos";
const IMAGE_TYPE = "Image";
const IMAGE_EXTS = new Set(["jpg", "jpeg", "png", "gif", "webp", "svg", "bmp", "heic", "heif"]);

const getExt = (file) => {
  const fromName = String(file?.name || file?.originalName || "").split(".").pop()?.trim().toLowerCase();
  if (fromName && IMAGE_EXTS.has(fromName)) return fromName;
  const fromMime = String(file?.type || file?.mimeType || "").split("/").pop()?.trim().toLowerCase();
  return fromMime || "";
};

const isImage = (file) => {
  const mime = String(file?.mimeType || file?.type || "").toLowerCase();
  if (mime.startsWith("image/")) return true;
  const ext = getExt(file);
  return IMAGE_EXTS.has(ext);
};

const formatSize = (bytes) => {
  if (!bytes || bytes === 0) return "0 B";
  const k = 1024;
  const sizes = ["B", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};

const getDisplayName = (file) => {
  if (file?.description?.trim()) return file.description;
  if (file?.originalName) return file.originalName;
  if (file?.name) return file.name;
  return "Untitled";
};

const PropertyImagesPanel = ({
  onClose,
  propertyInfo,
  uploadSingle,
  fileRefCollection = "propertyAssets",
  emptySelectionMessage = "Select property first",
  previewEntityType = "property",
  entityName = "",
}) => {
  const privateInfo = useSelector((s) => s.auth.PrivateInfo);
  const effectiveAuthorId = useMemo(
    () => privateInfo?.id || privateInfo?.userId || propertyInfo?.authorId || "",
    [privateInfo?.id, privateInfo?.userId, propertyInfo?.authorId]
  );

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(() => {
    try { return localStorage.getItem("cs_images_view") || "grid"; } catch { return "grid"; }
  });
  const [search, setSearch] = useState("");
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fetchingSatellite, setFetchingSatellite] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const fileInputRef = useRef(null);

  // Metadata dialog state (used for both upload prefill and inline edit)
  const [metaDialog, setMetaDialog] = useState({
    open: false,
    mode: "upload", // "upload" | "edit"
    pendingFile: null,
    file: null,
    name: "",
    description: "",
  });
  const closeMetaDialog = () => setMetaDialog((m) => ({ ...m, open: false, pendingFile: null, file: null }));

  useEffect(() => {
    try { localStorage.setItem("cs_images_view", view); } catch {}
  }, [view]);

  const refresh = useCallback(async () => {
    if (!propertyInfo?.id) {
      setImages([]);
      return;
    }
    setLoading(true);
    try {
      const result = await apiGetFiles({
        refCollection: fileRefCollection,
        refId: propertyInfo.id,
        authorId: effectiveAuthorId,
      });
      const all = result?.files || [];
      setImages(all.filter(isImage));
    } catch (e) {
      console.error("Failed to load images:", e);
      myToast.error("Failed to load images");
    } finally {
      setLoading(false);
    }
  }, [propertyInfo?.id, fileRefCollection, effectiveAuthorId]);

  useEffect(() => { refresh(); }, [refresh]);

  const performUpload = useCallback((file, name, description) => {
    if (!propertyInfo?.id) {
      myToast.error(emptySelectionMessage);
      return;
    }
    if (!effectiveAuthorId) {
      myToast.error("Missing author context. Please sign in again.");
      return;
    }
    if (!isImage(file)) {
      myToast.error("Only image files are allowed");
      return;
    }

    setUploadStatus("Uploading...");
    setUploadProgress(0);

    const fileId = uuid_v4();
    const domainKey = String(propertyInfo?.domain || "myrighthand").trim() || "myrighthand";
    const directory = `${domainKey}/${propertyInfo.id}/files`;
    const ext = getExt(file);
    const uploadKey = `${directory}/${fileId}`;
    const privateUrl = `${config.ENV.REACT_APP_HLS_BASE_URL}/${directory}/${fileId}`;
    const finalName = (name || "").trim() || file.name.replace(/\.[^/.]+$/, "");
    const finalDescription = (description || "").trim();

    uploadSingle(
      directory,
      file,
      fileId,
      (p) => setUploadProgress(p),
      async (err, result, done) => {
        if (err) {
          setUploadStatus("");
          setUploadProgress(0);
          myToast.error(err?.message || "Upload failed");
          return;
        }
        try {
          await apiCreateFile({
            authorId: effectiveAuthorId,
            fileCategory: IMAGE_CATEGORY,
            type: IMAGE_TYPE,
            fileType: ext,
            mimeType: file.type || `image/${ext || "jpeg"}`,
            url: privateUrl,
            key: uploadKey,
            size: String(file.size),
            description: finalName,
            data: finalDescription ? { description: finalDescription } : undefined,
            domain: propertyInfo?.domain || "",
            ref: { id: propertyInfo.id, collection: fileRefCollection },
            status: "active",
            isPublic: false,
          });
          await refresh();
          setUploadStatus("");
          setUploadProgress(0);
          myToast.success("Image uploaded");
        } catch (saveErr) {
          setUploadStatus("");
          console.error("Failed to save image:", saveErr);
          myToast.error(saveErr?.message || "Upload failed to save");
        }
        done && done();
      },
      { key: uploadKey, isPublic: false }
    );
  }, [propertyInfo?.id, propertyInfo?.domain, effectiveAuthorId, fileRefCollection, emptySelectionMessage, uploadSingle, refresh]);

  const openUploadDialog = (file) => {
    if (!hasEntity) {
      myToast.error(emptySelectionMessage);
      return;
    }
    if (!isImage(file)) {
      myToast.error("Only image files are allowed");
      return;
    }
    setMetaDialog({
      open: true,
      mode: "upload",
      pendingFile: file,
      file: null,
      name: file.name.replace(/\.[^/.]+$/, ""),
      description: "",
    });
  };

  const openEditDialog = (file) => {
    setMetaDialog({
      open: true,
      mode: "edit",
      pendingFile: null,
      file,
      name: getDisplayName(file),
      description: file?.data?.description || "",
    });
  };

  const handleMetaConfirm = async () => {
    const { mode, pendingFile, file, name, description } = metaDialog;
    if (mode === "upload" && pendingFile) {
      closeMetaDialog();
      performUpload(pendingFile, name, description);
      return;
    }
    if (mode === "edit" && file?.id) {
      try {
        const updated = await apiUpdateFile({
          id: file.id,
          authorId: file.authorId || effectiveAuthorId,
          description: name.trim() || getDisplayName(file),
          data: { ...(file.data || {}), description: description.trim() },
        });
        setImages((prev) => prev.map((f) => (f.id === file.id ? { ...f, ...updated, description: name.trim() || f.description, data: { ...(f.data || {}), description: description.trim() } } : f)));
        myToast.success("Image updated");
      } catch (e) {
        console.error("Failed to update image:", e);
        myToast.error("Failed to update image");
      }
      closeMetaDialog();
    }
  };

  const handleFilePick = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length) openUploadDialog(files[0]); // one at a time for metadata clarity
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files || []);
    if (files.length) openUploadDialog(files[0]);
  };

  const handleDelete = async (file) => {
    if (!file?.id) return;
    const authorId = file?.authorId || effectiveAuthorId;
    try {
      await apiDeleteFile(file.id, authorId);
      setImages((prev) => prev.filter((f) => f.id !== file.id));
      myToast.success("Image deleted");
    } catch (e) {
      console.error("Delete failed:", e);
      myToast.error("Delete failed");
    }
  };

  const handleFetchSatelliteImage = useCallback(async () => {
    if (!propertyInfo?.id) {
      myToast.error(emptySelectionMessage);
      return;
    }
    setFetchingSatellite(true);
    try {
      await apiGenerateSatelliteImage({
        propertyId: propertyInfo.id,
        addressIndex: 0,
      });
      await refresh();
      myToast.success("Satellite image fetched and saved");
    } catch (e) {
      console.error("Failed to fetch satellite image:", e);
      myToast.error(e?.message || "Failed to fetch satellite image");
    } finally {
      setFetchingSatellite(false);
    }
  }, [propertyInfo?.id, emptySelectionMessage, refresh]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const arr = q
      ? images.filter((f) => getDisplayName(f).toLowerCase().includes(q))
      : images;
    return [...arr].sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
  }, [images, search]);

  const hasEntity = !!propertyInfo?.id;
  const previewEntityIdProp = previewEntityType === "contact"
    ? { contactId: propertyInfo?.id }
    : { propertyId: propertyInfo?.id };

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        height: "100%",
        minHeight: 0,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: { xs: 0, sm: "8px" },
        backgroundColor: theme.palette.background.paper,
        overflow: "hidden",
      })}
    >
      <Box
        sx={(theme) => ({
          px: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 0.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: theme.palette.custom?.panelChrome || "#FFFFFF",
          ...addressBookPanelHeaderSx,
        })}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
          <Typography variant="caption" sx={{ color: "#64748b", fontSize: "11px" }}>
            {filtered.length} image{filtered.length !== 1 ? "s" : ""}
          </Typography>
          {entityName ? (
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                color: "text.primary",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: 260,
                ml: 0.5,
              }}
              title={entityName}
            >
              {entityName}
            </Typography>
          ) : null}
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <ToggleButtonGroup
            value={view}
            exclusive
            size="small"
            onChange={(_, v) => v && setView(v)}
            sx={{ height: 24, mr: 0.5 }}
          >
            <ToggleButton value="grid" sx={{ p: 0.4, border: "1px solid #E2E8F0" }} aria-label="Grid view">
              <GridViewIcon sx={{ fontSize: 14 }} />
            </ToggleButton>
            <ToggleButton value="large" sx={{ p: 0.4, border: "1px solid #E2E8F0" }} aria-label="Large view">
              <ViewModuleIcon sx={{ fontSize: 14 }} />
            </ToggleButton>
            <ToggleButton value="list" sx={{ p: 0.4, border: "1px solid #E2E8F0" }} aria-label="List view">
              <ViewListIcon sx={{ fontSize: 14 }} />
            </ToggleButton>
          </ToggleButtonGroup>
          {onClose ? (
            <Tooltip title="Close">
              <IconButton size="small" onClick={onClose} sx={{ width: 20, height: 20, p: 0, color: "#64748B" }}>
                <CloseIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      </Box>

      <Box sx={{ px: 1.5, py: 1, borderBottom: "1px solid #E2E8F0", display: "flex", gap: 0.75, alignItems: "center" }}>
        <TextField
          size="small"
          placeholder="Search images…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ fontSize: 14, color: "#94a3b8", mr: 0.5 }} />,
            sx: { fontSize: 12, height: 28 },
          }}
          sx={{ flex: 1 }}
        />
        <Button
          size="small"
          variant="outlined"
          onClick={handleFetchSatelliteImage}
          disabled={!hasEntity || fetchingSatellite}
          sx={{ whiteSpace: "nowrap", textTransform: "none", minWidth: 170 }}
        >
          {fetchingSatellite ? "Fetching..." : "Fetch Satellite Image"}
        </Button>
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={handleFilePick}
      />

      <Box
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (hasEntity) setIsDragOver(true); }}
        onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false); }}
        onDrop={hasEntity ? handleDrop : undefined}
        onClick={() => hasEntity && fileInputRef.current?.click()}
        sx={{
          mx: 1.5,
          my: 1,
          py: 1.5,
          border: `1px dashed ${isDragOver ? "#2563EB" : "#CBD5E1"}`,
          borderRadius: "8px",
          backgroundColor: isDragOver ? "#EFF6FF" : hasEntity ? "#F8FAFC" : "#F1F5F9",
          color: "#64748B",
          textAlign: "center",
          cursor: hasEntity ? "pointer" : "not-allowed",
          opacity: hasEntity ? 1 : 0.6,
          transition: "all 0.15s ease",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 22, color: isDragOver ? "#2563EB" : "#94A3B8" }} />
        <Typography sx={{ fontSize: 12 }}>
          {hasEntity ? "Drop images here or click to upload" : emptySelectionMessage}
        </Typography>
        <Typography sx={{ fontSize: 10, color: "#94A3B8" }}>
          Saved as {IMAGE_CATEGORY} · {IMAGE_TYPE}
        </Typography>
      </Box>

      {uploadStatus ? (
        <Box sx={{ px: 1.5, pb: 1 }}>
          <LinearProgress variant="determinate" value={uploadProgress} sx={{ height: 4, borderRadius: 2 }} />
          <Typography sx={{ fontSize: 10, color: "#64748B", mt: 0.25 }}>
            {uploadStatus} {uploadProgress}%
          </Typography>
        </Box>
      ) : null}

      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 1.5, pb: 1.5 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={20} />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4, color: "#94A3B8", fontSize: 12 }}>
            {hasEntity ? "No images yet" : emptySelectionMessage}
          </Box>
        ) : view === "grid" ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(120px, 1fr))",
              gap: 1,
            }}
          >
            {filtered.map((file) => (
              <ImageTile
                key={file.id}
                file={file}
                onPreview={() => setPreviewFile(file)}
                onDelete={() => handleDelete(file)}
                onEdit={() => openEditDialog(file)}
              />
            ))}
          </Box>
        ) : view === "large" ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
              gap: 1.5,
            }}
          >
            {filtered.map((file) => (
              <ImageTile
                key={file.id}
                file={file}
                onPreview={() => setPreviewFile(file)}
                onDelete={() => handleDelete(file)}
                size="large"
              />
            ))}
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            {filtered.map((file) => (
              <ImageRow
                key={file.id}
                file={file}
                onPreview={() => setPreviewFile(file)}
                onDelete={() => handleDelete(file)}
                onEdit={() => openEditDialog(file)}
              />
            ))}
          </Box>
        )}
      </Box>

      {previewFile ? (
        <PreviewFile
          file={previewFile}
          open={!!previewFile}
          onClose={() => setPreviewFile(null)}
          authorId={effectiveAuthorId}
          {...previewEntityIdProp}
        />
      ) : null}

      <Dialog open={metaDialog.open} onClose={closeMetaDialog} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600 }}>
          {metaDialog.mode === "upload" ? "Add image details" : "Edit image"}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {metaDialog.mode === "edit" && metaDialog.file?.url ? (
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
              <Box sx={{ width: 96, height: 96, borderRadius: 1, overflow: "hidden", border: "1px solid #E2E8F0" }}>
                <img src={metaDialog.file.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </Box>
            </Box>
          ) : null}
          <TextField
            autoFocus
            label="Name"
            value={metaDialog.name}
            onChange={(e) => setMetaDialog((m) => ({ ...m, name: e.target.value }))}
            fullWidth
            size="small"
            sx={{ mb: 1.5 }}
          />
          <TextField
            label="Description"
            value={metaDialog.description}
            onChange={(e) => setMetaDialog((m) => ({ ...m, description: e.target.value }))}
            fullWidth
            size="small"
            multiline
            minRows={3}
            maxRows={6}
            placeholder="Optional notes about this image"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeMetaDialog} size="small">Cancel</Button>
          <Button onClick={handleMetaConfirm} variant="contained" size="small">
            {metaDialog.mode === "upload" ? "Upload" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const ImageTile = ({ file, onPreview, onDelete, onEdit, size = "small" }) => {
  const [errored, setErrored] = useState(false);
  const isLarge = size === "large";
  const desc = file?.data?.description || "";
  return (
    <Box
      sx={{
        position: "relative",
        aspectRatio: "1 / 1",
        borderRadius: isLarge ? "10px" : "6px",
        overflow: "hidden",
        border: "1px solid #E2E8F0",
        backgroundColor: "#F8FAFC",
        cursor: "pointer",
        "&:hover .img-overlay": { opacity: 1 },
      }}
      onClick={onPreview}
      title={getDisplayName(file)}
    >
      {errored ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#CBD5E1" }}>
          <BrokenImageIcon sx={{ fontSize: isLarge ? 48 : 24 }} />
        </Box>
      ) : (
        <img
          src={file.url}
          alt={getDisplayName(file)}
          onError={() => setErrored(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      )}
      <Box
        className="img-overlay"
        sx={{
          position: "absolute",
          inset: 0,
          background: "linear-gradient(to top, rgba(0,0,0,0.6), rgba(0,0,0,0) 55%)",
          opacity: 0,
          transition: "opacity 0.15s ease",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          p: isLarge ? 1 : 0.5,
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.25 }}>
          {onEdit ? (
            <IconButton
              size="small"
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
              sx={{
                width: isLarge ? 30 : 22,
                height: isLarge ? 30 : 22,
                p: 0,
                color: "#FFFFFF",
                backgroundColor: "rgba(0,0,0,0.4)",
                "&:hover": { backgroundColor: "rgba(37,99,235,0.85)" },
              }}
              aria-label="Edit"
            >
              <EditIcon sx={{ fontSize: isLarge ? 16 : 12 }} />
            </IconButton>
          ) : null}
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); onDelete(); }}
            sx={{
              width: isLarge ? 30 : 22,
              height: isLarge ? 30 : 22,
              p: 0,
              color: "#FFFFFF",
              backgroundColor: "rgba(0,0,0,0.4)",
              "&:hover": { backgroundColor: "rgba(220,38,38,0.85)" },
            }}
          >
            <DeleteIcon sx={{ fontSize: isLarge ? 16 : 12 }} />
          </IconButton>
        </Box>
        <Box>
          <Typography
            sx={{
              color: "#FFFFFF",
              fontSize: isLarge ? 13 : 10,
              fontWeight: 500,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {getDisplayName(file)}
          </Typography>
          {isLarge && desc ? (
            <Typography
              sx={{
                color: "rgba(255,255,255,0.85)",
                fontSize: 11,
                mt: 0.25,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {desc}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

const ImageRow = ({ file, onPreview, onDelete, onEdit }) => {
  const [errored, setErrored] = useState(false);
  const desc = file?.data?.description || "";
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        p: 0.5,
        border: "1px solid #E2E8F0",
        borderRadius: "6px",
        cursor: "pointer",
        "&:hover": { backgroundColor: "#F8FAFC" },
      }}
      onClick={onPreview}
    >
      <Box sx={{ width: 40, height: 40, borderRadius: "4px", overflow: "hidden", backgroundColor: "#F1F5F9", flexShrink: 0 }}>
        {errored ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: "#CBD5E1" }}>
            <BrokenImageIcon sx={{ fontSize: 18 }} />
          </Box>
        ) : (
          <img
            src={file.url}
            alt={getDisplayName(file)}
            onError={() => setErrored(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        )}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {getDisplayName(file)}
        </Typography>
        {desc ? (
          <Typography
            sx={{
              fontSize: 11,
              color: "#475569",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {desc}
          </Typography>
        ) : null}
        <Typography sx={{ fontSize: 10, color: "#94A3B8" }}>
          {formatSize(Number(file.size) || 0)}
          {file.createdAt ? ` · ${new Date(file.createdAt).toLocaleDateString()}` : ""}
        </Typography>
      </Box>
      {onEdit ? (
        <Tooltip title="Edit">
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onEdit(); }} sx={{ width: 24, height: 24, p: 0, color: "#64748B" }}>
            <EditIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Tooltip>
      ) : null}
      <Tooltip title="Preview">
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onPreview(); }} sx={{ width: 24, height: 24, p: 0, color: "#64748B" }}>
          <PreviewIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }} sx={{ width: 24, height: 24, p: 0, color: "#DC2626" }}>
          <DeleteIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default PropertyImagesPanel;
